/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * Models the CodeTableType
 *
 * @class CodeTableType
 */
class CodeTableType {
  /**
   * Creates an instance of CodeTableType.
   * @param {*} json
   * @memberof CodeTableType
   */
  constructor(json = null) {
    if (json) {
      // string
      this.tableName = json.tableName;

      // string
      this.value = json.value;

      // string
      this.description = json.description;

      // string
      this.parentCodeTable = json.parentCodeTable;
    }
  }
}

/**
 * Models the UAPaymentDetail
 *
 * @class UAPaymentDetail
 */
class UAPaymentDetail {
  /**
   * Creates an instance of UAPaymentDetail.
   * @param {*} json
   * @memberof UAPaymentDetail
   */
  constructor(json = null) {
    if (json) {
      // string
      this.benefitName = json.benefitName;

      // string
      this.caseId = json.caseId;

      // string
      this.caseReference = json.caseReference;

      // string
      this.coverEndDate = json.coverEndDate;

      // string
      this.coverStartDate = json.coverStartDate;

      // number
      this.creditAmount = json.creditAmount;

      // number
      this.debitAmount = json.debitAmount;

      // object - CodeTableType
      if (json.entitlement) {
        this.entitlement = new CodeTableType(json.entitlement);
      }
    }
  }
}

/**
 * Models the UAPayment
 *
 * @class UAPayment
 */
class UAPayment {
  /**
   * Creates an instance of UAPayment.
   * @param {*} json
   * @memberof UAPayment
   */
  constructor(json = null) {
    if (json) {
      // number
      this.amount = json.amount;

      // string
      this.bankAccountNumber = json.bankAccountNumber;

      // string
      this.bankSortCode = json.bankSortCode;

      // string
      this.checkNumber = json.checkNumber;

      // string
      this.coverStartDate = json.coverStartDate;

      // string
      this.coverEndDate = json.coverEndDate;

      // object - CodeTableType
      if (json.currency) {
        this.currency = new CodeTableType(json.currency);
      }

      // string
      this.dueDate = json.dueDate;

      // object - CodeTableType
      if (json.method) {
        this.method = new CodeTableType(json.method);
      }

      // string
      this.nominatedPayeeAddress = json.nominatedPayeeAddress;

      // string
      this.nominatedPayeeName = json.nominatedPayeeName;

      // string
      this.payedByName = json.payedByName;

      // string
      this.payment_id = json.payment_id;

      // string
      this.paymentDate = json.paymentDate;

      // object - CodeTableType
      if (json.status) {
        this.status = new CodeTableType(json.status);
      }

      // object - CodeTableType
      if (json.statusOther) {
        this.statusOther = new CodeTableType(json.statusOther);
      }

      // boolean
      this.external = json.external;

      // string
      this.voucherNumber = json.voucherNumber;

      // array - UAPaymentDetail
      if (json.paymentDetails) {
        this.paymentDetails = json.paymentDetails.map(object => new UAPaymentDetail(object));
      } else if (json.constructor === Array) {
        this.paymentDetails = json.map(object => new UAPaymentDetail(object));
      }
    }
  }
}

/**
 * Models the UAPaymentList
 *
 * @class UAPaymentList
 */
class UAPaymentList {
  /**
   * Creates an instance of UAPaymentList.
   * @param {*} json
   * @memberof UAPaymentList
   */
  constructor(json = null) {
    if (json) {
      // array - UAPayment
      if (json.data) {
        this.data = json.data.map(object => new UAPayment(object));
      } else if (json.constructor === Array) {
        this.data = json.map(object => new UAPayment(object));
      }
    }
  }
}

export default {
  CodeTableType,

  UAPaymentDetail,

  UAPayment,

  UAPaymentList,
};
